import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Page, Layout, Card, Filters, Badge, Banner, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { fetchOrders, fetchNightMode, setFilterValue } from "containers/orders/orders.slice";
import DataTable from "components/datatable/Datatable";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { AppDispatch, RootState } from "configureStore";
import { getZoneColor, getTextColor, directionKey, directionBadge } from "utils/helperFunctions";


function Orders() {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const ordersCollection = useSelector(
    (state: RootState) => state.ordersReducer.ordersCollection
  );

  const filtersValue = useSelector(
    (state: RootState) => state.ordersReducer.filtersValue
  );

  const nightMode = useSelector(
    (state: RootState) => state.ordersReducer.nightMode
  );


  const disambiguateLabel = (key: string, value: string) => {
    switch (key) {
      case "textSearch":
        return `${t("search")}: ${t(value)}`;
      default:
        return value;
    }
  };

  // Auto-refresh every 10 seconds - night mode rides the same timer, it does
  // not get a second interval of its own.
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchOrders());
      dispatch(fetchNightMode());
    }, 10000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Initial data fetch
  useEffect(() => {
    dispatch(fetchOrders());
    dispatch(fetchNightMode());
  }, [dispatch]);

  const handleFilterChange = (key: string, value: string | null) => {
    dispatch(setFilterValue({ key, value }));
  };

  const handleQueryChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleQueryValueRemove = () => {
    setSearchQuery("");
  };

  const handleClearAll = () => {
    // Optional: clear all filters if needed
  };

  // Format products HTML with ADR badge - mobile optimized
  const formatProductsHTML = (products: any[], isAdr: boolean = false) => {
    if (!products || products.length === 0) return '-';

    const productsHTML = products
      .map((product: any) =>
        `${product?.secondary_code || ''} ${product?.code || ''} ${product?.title || ''} ${product?.measure || ''} ${product?.amount || ''}`
      )
      .join('<br>');

    const adrBadge = isAdr ? '<span style="background-color: #008060; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; margin-left: 8px;">ADR</span>' : '';

    return `<div style="cursor: pointer; font-size: 14px; line-height: 1.4;"><div>${productsHTML}${adrBadge}</div></div>`;
  };

  // Format signature status badge
  const formatSignatureStatus = (isSigned: boolean) => {
    return (
      <Badge tone={isSigned ? "success" : "critical"}>
        {isSigned ? t("signed") : t("not_signed")}
      </Badge>
    );
  };

  //Frontend search filtering and sorting
  const filteredCollection = useMemo(() => {
    let filtered = ordersCollection;

    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item: any) => {
        // Search across all visible fields
        const searchableFields = [
          item.date,
          item.orderId?.toString(),
          // item.customer,
          // item.title,
          item.nr,
          item.driver,
          // item.type,
          item.terminal_number,
          item.terminal_zone,
          item.terminal_status
        ];

        return searchableFields?.some(field =>
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Sort: transports without terminal number first, then by terminal number DESC
    return [...filtered]?.sort((a: any, b: any) => {
      const aHasNumber = !!(a?.terminal_number);
      const bHasNumber = !!(b?.terminal_number);

      if (!aHasNumber && bHasNumber) return -1;
      if (aHasNumber && !bHasNumber) return 1;

      if (aHasNumber && bHasNumber) {
        return b.terminal_number.localeCompare(a.terminal_number, undefined, { numeric: true });
      }

      // If neither has terminal_number, maintain original order
      return 0;
    });
  }, [ordersCollection, searchQuery]);

  const appliedFilters = Object.keys(filtersValue)
    .filter((key) => filtersValue[key]?.length > 0)
    .map((key) => ({
      key,
      label: disambiguateLabel(key, filtersValue[key]),
      onRemove: () => handleFilterChange(key, null),
    }));

  // Add search query to applied filters if it exists
  if (searchQuery.trim()) {
    appliedFilters.push({
      key: "textSearch",
      label: `${t("search")}: ${searchQuery}`,
      onRemove: handleQueryValueRemove,
    });
  }

  const onRowClick = (item: any) => {
    dispatch(setModalxComponent("OrderReleaseForm"));
    dispatch(setModalxComponentProps(item));
  };

  const onSignatureClick = (item: any) => {
    dispatch(setModalxComponent("SignatureModal"));
    dispatch(setModalxComponentProps({
      id_upload_transport: item.id,
      type: item.type
    }));
  };

  const onProductClick = (item: any) => {
    // For now, just trigger the same action as row click
    onRowClick(item);
  };

  const filters: any[] = [];

  const collection = filteredCollection.map((item: any) => ({
    ...item,
    // Žvaigždutė rodoma ATSKIRAME rakte: `nr` keliauja toliau į išleidimo
    // modalą kaip valstybinis numeris, jo dekoruoti negalima.
    nr_display: item.is_priority ? `\u2605 ${item.nr || ''}` : item.nr,
    assigned_user_name: item.assign_user ?
      `${item.assign_user.name_user || ''} ${item.assign_user.surname_user || ''}`.trim() :
      '',
    products: formatProductsHTML(item.products || [], item.is_adr),
    rules_signed_status: (
      <div style={{ cursor: "pointer" }} onClick={() => onSignatureClick(item)}>
        {formatSignatureStatus(item.is_signed)}
      </div>
    ),
    terminal_zone_styled: (() => {
      if (!item.terminal_zone) {
        return `<div style="
          background-color: #f0f0f0;
          color: #666;
          padding: 4px 8px;
          border-radius: 4px;
          text-align: center;
          font-weight: bold;
        ">-</div>`;
      }

      const bgColor = getZoneColor(item.terminal_zone);
      const textColor = getTextColor(bgColor);
      return `<div style="
        background-color: ${bgColor};
        color: ${textColor};
        padding: 4px 8px;
        border-radius: 4px;
        text-align: center;
        font-weight: bold;
      ">${item.terminal_zone}</div>`;
    })(),
    // Both directions store the same action names, so the label follows the
    // row's direction and falls back to the shared key.
    terminal_status_display: item.terminal_status
      ? t(directionKey(item.terminal_status, item.type), {
          defaultValue: t(item.terminal_status, {
            defaultValue: item.terminal_status.replace(/_/g, " ").toUpperCase(),
          }),
        })
      : "-",
    direction_styled: directionBadge(item.type),
  }));

  return (
    <Layout>
      <Layout.Section>
        <Page
          fullWidth
          title={t("security_control")}
        >
          {nightMode?.active && nightMode?.available && (
            <div style={{ marginBottom: "16px" }}>
              <Banner tone="warning">
                <Text as="p" variant="bodyMd">
                  {t("night_mode_on_guard")}
                </Text>
                <Text as="p" variant="bodyMd">
                  {/* expires_at is already local server time - slice it, never new Date() it */}
                  {t("night_mode_until")}: {nightMode.expires_at ? nightMode.expires_at.slice(11, 16) : "-"}
                </Text>
              </Banner>
            </div>
          )}
          <Card>
            <div style={{ padding: "16px", display: "flex" }}>
              <div style={{ flex: 1 }}>
                <Filters
                  queryValue={searchQuery}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={handleQueryChange}
                  onQueryClear={handleQueryValueRemove}
                  onClearAll={handleClearAll}
                />
              </div>
            </div>
            <DataTable
              collection={collection}
              columns={[
                {
                  key: "direction_styled",
                  name: t("direction"),
                  type: "html_node",
                  onClick: onRowClick,
                },
                {
                  key: "terminal_number",
                  name: t("terminal_number"),
                  mobileView: "topRight",
                  onClick: onRowClick,
                },
                {
                  key: "terminal_zone_styled",
                  name: t("terminal_zone"),
                  mobileView: "topRight",
                  type: "html_node",
                  onClick: onRowClick,
                },
                {
                  // The badge was computed but had no column, so SignatureModal
                  // was unreachable and the rules signature could not be
                  // requested from the guard booth at all.
                  key: "rules_signed_status",
                  name: t("rules_signed"),
                  type: "react_node",
                },
                {
                  key: "terminal_status_display",
                  name: t("terminal_status"),
                  mobileView: "bottomRight",
                  onClick: onRowClick,
                },
                {
                  key: "date",
                  name: t("date"),
                  type: "clickabledate",
                  mobileView: "bottomRight",
                  onClick: onRowClick
                },
                {
                  key: "orderId",
                  name: t("order_number"),
                  mobileView: "topLeft",
                  onClick: onRowClick,
                },
                {
                  key: "nr_display",
                  name: t("truck_plate_number"),
                  mobileView: "topLeft",
                  onClick: onRowClick,
                },
                {
                  key: "driver",
                  name: t("driver_name"),
                  mobileView: "topLeft",
                  onClick: onRowClick,
                },
                {
                  key: "products",
                  name: t("goods"),
                  type: "html_node",
                  mobileView: "bottomLeft",
                  onClick: onProductClick,
                }
              ]}
              getRowStyle={(item: any) => {
                // Prioritetinė mašina eina pirma: ji sako, ką aptarnauti be eilės,
                // todėl violetinė nugali žalią „taisyklės pasirašytos" spalvą.
                if (item.is_priority) {
                  return {
                    backgroundColor: '#f7f1fd',
                    borderLeft: '4px solid #7c3aed',
                  };
                }
                if (item.is_signed) {
                  return {
                    backgroundColor: '#e8f5e8', // Light green background
                    borderLeft: '4px solid #28a745', // Green left border
                  };
                }
                return {};
              }}
            />
          </Card>
        </Page>
      </Layout.Section>
    </Layout>
  );
}

export default Orders;
